import useProfile from "@/hooks/use-profile";
import useApi from "@/hooks/useApi";
import RoundRobinRepository from "@/utilities/repositories/RoundRobin";
import UserRepository from "@/utilities/repositories/Users";
import {
  ETimeSlotConfigMode,
  ITimeSlotConfig,
} from "@/utilities/types/RoundRobin";
import { IResponseData } from "@/utilities/types/requests";
import { IUserDataResponse } from "@/utilities/types/Users";
import { useEffect, useState, useMemo, useCallback, memo } from "react";
import toast from "react-hot-toast";
import { Select, SelectOption, Switch } from "rizzui";
import { isOtpFeatureEnabled } from "@/utilities/functions/features";
import cn from "@/utils/class-names";

export interface TimeSlotConfigModeProps {
  updateMode: (mode: ETimeSlotConfigMode) => void;
}

const MODE_LABELS: Record<string, string> = {
  [ETimeSlotConfigMode.ROUND_ROBIN]: "Time-based",
  [ETimeSlotConfigMode.DEFAULT]: "Default",
  [ETimeSlotConfigMode.OFF]: "Off",
};

// ============================================================================
// Child Components with React.memo
// ============================================================================

interface ModeSelectorProps {
  value: ETimeSlotConfigMode;
  disabled?: boolean;
  onChange: (value: string) => void;
}

const ModeSelector = memo(function ModeSelector({
  value,
  disabled,
  onChange,
}: ModeSelectorProps) {
  return (
    <div className="rounded-lg border border-gray-200 bg-white p-5 shadow-sm">
      <Select
        disabled={disabled}
        options={[
          { value: ETimeSlotConfigMode.OFF, label: "Off" },
          { value: ETimeSlotConfigMode.DEFAULT, label: "Default" },
          { value: ETimeSlotConfigMode.ROUND_ROBIN, label: "Time-based" },
        ]}
        label="Round Robin Mode"
        onChange={onChange}
        getOptionValue={(option: SelectOption) => option.value}
        displayValue={(value: string) => MODE_LABELS[value] || ""}
        value={value}
      />
    </div>
  );
});

interface VerifiedLeadsToggleProps {
  checked: boolean;
  disabled?: boolean;
  onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
}

const VerifiedLeadsToggle = memo(function VerifiedLeadsToggle({
  checked,
  disabled,
  onChange,
}: VerifiedLeadsToggleProps) {
  return (
    <div
      className={cn(
        "group rounded-lg border border-gray-200 bg-white p-4 shadow-sm transition-all hover:border-gray-300",
        disabled && "opacity-60 cursor-not-allowed",
      )}
    >
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1 space-y-1.5">
          <div className="flex items-center gap-2">
            <label className="text-sm font-medium text-gray-700">
              Receive Only Verified Leads
            </label>
          </div>
          <p className="text-xs text-gray-500 leading-relaxed">
            Skip unverified leads in round robin distribution to ensure quality
            lead assignment
          </p>
        </div>
        <div className="flex-shrink-0">
          <Switch disabled={disabled} checked={checked} onChange={onChange} />
        </div>
      </div>
    </div>
  );
});

interface ExcludeMainAccountToggleProps {
  checked: boolean;
  disabled?: boolean;
  subAccountsCount: number;
  onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
}

const ExcludeMainAccountToggle = memo(function ExcludeMainAccountToggle({
  checked,
  disabled,
  subAccountsCount,
  onChange,
}: ExcludeMainAccountToggleProps) {
  const hasNoTeamMembers = subAccountsCount === 0;

  return (
    <div
      className={cn(
        "group rounded-lg border transition-all",
        hasNoTeamMembers
          ? "border-gray-200 bg-gray-50"
          : "border-gray-200 bg-white shadow-sm hover:border-gray-300",
        disabled && "opacity-60 cursor-not-allowed",
      )}
    >
      <div className="p-4">
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1 space-y-1.5">
            <div className="flex items-center gap-2">
              <label
                className={cn(
                  "text-sm font-medium",
                  hasNoTeamMembers ? "text-gray-500" : "text-gray-700",
                )}
              >
                Exclude Main Account from Lead Assignment
              </label>
            </div>
            <p
              className={cn(
                "text-xs leading-relaxed",
                hasNoTeamMembers ? "text-gray-400" : "text-gray-500",
              )}
            >
              Only team members will receive leads. Main account will be
              excluded from distribution.
            </p>
            {hasNoTeamMembers && (
              <div className="mt-2 flex items-start gap-2 rounded-md bg-amber-50 border border-amber-200 p-2.5">
                <p className="text-xs text-amber-700 leading-relaxed">
                  No team members found. Please add team members first to enable
                  this feature.
                </p>
              </div>
            )}
          </div>
          <div className="flex-shrink-0">
            <Switch
              disabled={disabled || hasNoTeamMembers}
              checked={checked}
              onChange={onChange}
            />
          </div>
        </div>
      </div>
    </div>
  );
});

// ============================================================================
// Main Component
// ============================================================================

export default function TimeSlotConfigMode(props: TimeSlotConfigModeProps) {
  const { updateMode } = props;
  const { profile } = useProfile();
  const [subAccountsCount, setSubAccountsCount] = useState(0);
  const [localConfig, setLocalConfig] = useState<ITimeSlotConfig | undefined>();

  const {
    request: getTimeSlotConfig,
    response,
    loading,
    refetch,
  } = useApi({
    request: RoundRobinRepository.getTimeSlotConfig,
  });

  const { request: updateTimeSlotConfig, loading: updating } = useApi({
    request: RoundRobinRepository.updateTimeSlotConfig,
  });

  const { request: getTeamUsers } = useApi<IResponseData<IUserDataResponse>>({
    request: UserRepository.getTeamUsers,
  });

  // Sync response to local config
  useEffect(() => {
    if (response?.data) {
      setLocalConfig(response.data);
    }
  }, [response?.data]);

  useEffect(() => {
    if (!profile?.id) return;

    // Fetch sub-accounts count
    getTeamUsers({ limit: 1, page: 1 })
      .then((res) => {
        setSubAccountsCount(res?.data?.total || 0);
      })
      .catch(() => {
        setSubAccountsCount(0);
      });

    getTimeSlotConfig(profile.id)
      .then(async (response) => {
        if (!response?.data) {
          // Means no data found => create new config
          await updateTimeSlotConfig({
            id: profile.id,
            data: {
              mode: ETimeSlotConfigMode.OFF,
            },
          });
          const res = await refetch();
          updateMode(res?.data?.mode || ETimeSlotConfigMode.OFF);
        } else {
          updateMode(response.data.mode || ETimeSlotConfigMode.OFF);
        }
      })
      .catch(() => {
        // Silently handle error - config will be created on first access
      });
  }, [profile?.id]);

  const handleModeChange = useCallback(
    async (value: string) => {
      if (!profile?.id) {
        toast.error("Not found agency profile");
        return;
      }

      // Optimistic update
      setLocalConfig((prev) =>
        prev ? { ...prev, mode: value as ETimeSlotConfigMode } : undefined,
      );
      updateMode(value as ETimeSlotConfigMode);

      try {
        await updateTimeSlotConfig({
          id: profile.id,
          data: {
            mode: value,
          },
        });
        toast.success("Mode updated successfully");
      } catch (error) {
        // Rollback on error
        const res = await refetch();
        setLocalConfig(res?.data);
        updateMode(res?.data?.mode || ETimeSlotConfigMode.OFF);
        toast.error("Failed to update mode");
      }
    },
    [profile?.id, updateTimeSlotConfig, refetch, updateMode],
  );

  const handleToggleIgnoreUnverified = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      const checked = event.target.checked;

      if (!profile?.id) {
        toast.error("Not found agency profile");
        return;
      }

      // Optimistic update
      setLocalConfig((prev) =>
        prev
          ? {
              ...prev,
              ignoreUnverifiedLeads: checked,
            }
          : undefined,
      );

      try {
        await updateTimeSlotConfig({
          id: profile.id,
          data: {
            ignoreUnverifiedLeads: checked,
            excludeMainAccount: localConfig?.excludeMainAccount || false,
            mode: localConfig?.mode || ETimeSlotConfigMode.OFF,
          },
        });
        toast.success(
          checked
            ? "Unverified leads will be ignored"
            : "Unverified leads will be included",
        );
      } catch (error) {
        // Rollback on error
        const res = await refetch();
        setLocalConfig(res?.data);
        toast.error("Failed to update setting");
      }
    },
    [profile?.id, localConfig?.mode, updateTimeSlotConfig, refetch],
  );

  const handleToggleExcludeMainAccount = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      const checked = event.target.checked;

      if (!profile?.id) {
        toast.error("Not found agency profile");
        return;
      }

      // Validate sub-accounts exist before enabling
      if (checked && subAccountsCount === 0) {
        toast.error(
          "Cannot enable: No sub-accounts available. Please add team members first.",
        );
        return;
      }

      // Optimistic update
      setLocalConfig((prev) =>
        prev
          ? {
              ...prev,
              excludeMainAccount: checked,
            }
          : undefined,
      );

      try {
        await updateTimeSlotConfig({
          id: profile.id,
          data: {
            excludeMainAccount: checked,
            ignoreUnverifiedLeads: localConfig?.ignoreUnverifiedLeads || false,
            mode: localConfig?.mode || ETimeSlotConfigMode.OFF,
          },
        });
        toast.success(
          checked
            ? "Main account excluded from lead assignment"
            : "Main account included in lead assignment",
        );
      } catch (error) {
        // Rollback on error
        const res = await refetch();
        setLocalConfig(res?.data);
        toast.error("Failed to update setting");
      }
    },
    [
      profile?.id,
      subAccountsCount,
      localConfig?.mode,
      updateTimeSlotConfig,
      refetch,
    ],
  );

  const isOtpEnabled = useMemo(
    () => isOtpFeatureEnabled(localConfig?.userFeatures),
    [localConfig?.userFeatures],
  );

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
      {/* Mode Selection */}
      <ModeSelector
        value={localConfig?.mode || ETimeSlotConfigMode.OFF}
        onChange={handleModeChange}
      />

      {/* OTP Verification Setting */}
      {isOtpEnabled && (
        <VerifiedLeadsToggle
          checked={localConfig?.ignoreUnverifiedLeads || false}
          onChange={handleToggleIgnoreUnverified}
        />
      )}

      {/* Exclude Main Account Setting */}
      <ExcludeMainAccountToggle
        checked={localConfig?.excludeMainAccount || false}
        subAccountsCount={subAccountsCount}
        onChange={handleToggleExcludeMainAccount}
      />
    </div>
  );
}
